import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Field,
  FieldArray,
  formValueSelector,
  change,
  Fields,
  FormSection
} from "redux-form";
import api from "APP_ROOT/api";
import removeKey from "LIB/removeKey";
import _, { isEmpty } from "lodash";
import { fobFormat } from "LIB/formatters/address";
import { dollars } from "LIB/formatters/currency";
import DoNotDoBusinessWarning from "MODULES/companies/shared/components/DoNotDoBusinessWarning";
import { REPAIR_STATION } from "MODULES/companies/constants";
import {
  CAAC,
  EASA,
  FAA,
  INVENTORIES,
  OTHER,
  OTHER_TAG_TYPE,
  TCCA,
  UK_CAA
} from "MODULES/inventories/constants";
import {
  ACTIONS_REQUESTED,
  EXPECTED_COMPONENT,
  TEARDOWN,
  REPAIR_EVALUATION_APPROVED,
  REPAIR_EVALUATION_REJECTED
} from "MODULES/RepairOrders/constants";
import { convertRo } from "MODULES/RepairOrders/actions";
import { DashedSeparator } from "MODULES/shared/components";
import {
  form,
  Form as AppForm,
  FormRow,
  FormInput,
  TextArea
} from "MODULES/shared/components/Forms";
import ShippingFields from "MODULES/Orders/shared/ShippingFields";
import SearchCompany from "MODULES/companies/shared/FindOrCreateCompanyToggle/SearchCompany";
import { searchRepairStationCompanies } from "MODULES/companies/actions";
import {
  searchInventories as queryInventories,
  searchPartsByStatus,
  searchContacts
} from "MODULES/parts/actions/actions";
import { Col } from "react-bootstrap";
import Warning from "MODULES/shared/components/Warning/Warning";
import {
  CAP_TYPES,
  COMPANY,
  DO_NOT_SHIP,
  IRE,
  SGP,
  USA,
  DROPSHIP,
  EXTERNAL_NOTE,
  CAP_TYPE
} from "MODULES/PurchaseOrders/constants";
import { selectPrimaryContact } from "MODULES/companies/helpers";
import {
  getCreditRequestedWarrantyRmas,
  getWarrantyAndNotCreditRmas
} from "MODULES/shared/helpers";
import {
  CONTACT_ID,
  LINE_ITEMS,
  PAYMENT_TERMS,
  RETURN_TO,
  RETURN_TO_COMPANY_ADDRESS_ID,
  RETURN_TO_COMPANY_ID,
  RETURN_TO_FOB,
  RETURN_TO_LOCATION_ID,
  RETURN_TO_SHIPPING_ADDRESS,
  RETURN_TO_SHIPPING_METHOD,
  RETURN_TO_TYPE,
  SHIP_TO_COMPANY_ADDRESS_ID,
  SHIP_TO_COMPANY_ID,
  SHIP_TO_FOB,
  SHIP_TO_SHIPPING_ADDRESS,
  SHIP_TO_SHIPPING_METHOD,
  SHIP_TO_TYPE
} from "APP_ROOT/constants";
import { properCase } from "LIB/formatters/text";
import { INTERNAL_NOTE } from "MODULES/shared/constants";
import TagTypes from "MODULES/inventories/components/EditInventory/TagTypes";
import MultiLocationFeature from "MODULES/shared/components/MultiLocationFeature/MultiLocationFeature";
import PaymentTerms from "MODULES/shared/components/PaymentTerms/PaymentTerms";
import { ActionRequestedSection } from "../Detail/LineItems/FormSections";
import validate from "./validate";

const FORM_NAME = "convertToRO";

const LocationWarning = ({ lineItems }) => {
  const sameStoreOwner =
    [...new Set(lineItems.map(a => a.store_owner_id))].length === 1;

  if (sameStoreOwner) return null;

  return (
    <Warning>
      Repair Order contains items from different store owners. Please ensure all
      items in your cart belong to the same store owner before proceeding.
    </Warning>
  );
};

LocationWarning.propTypes = {
  lineItems: PropTypes.arrayOf(PropTypes.shape({}))
};

export class Form extends React.Component {
  state = {
    contacts: [],
    invalidCompany: false,
    paymentTerms: null
  };

  componentWillMount() {
    if (this.props.selectedCompany && this.props.selectedCompany.id) {
      this.handleCompanySelected(this.props.selectedCompany);
      this.fetchContacts(this.props.selectedCompany.id);
    }
  }

  handleCompanySelected = company => {
    this.props.setSelectedCompany(company);
    this.fetchAddresses(company.id);

    this.props.setFieldValue(PAYMENT_TERMS, company.vendor_credit_terms);
  };

  fetchAddresses = companyId => {
    const { change: updateField } = this.props;
    api.companies.physicalAddresses
      .search(companyId, {
        page: 1,
        limit: 100,
        sort: "primary",
        reverse: true
      })
      .then(({ data: { results } }) => {
        if (results.length > 0) {
          const primaryAddress = results[0];
          this.setState({ addresses: results });

          // TODO: COME BACK TO THIS
          updateField("repair_station_physical_address_id", primaryAddress.id);
          updateField(RETURN_TO_FOB, fobFormat(primaryAddress));
        }
      });
  };

  fetchContacts = value => {
    if (!value) {
      return null;
    }
    return searchContacts(value).then((vals = []) => {
      this.setState({ contacts: vals });
      selectPrimaryContact(vals, val => this.props.change(CONTACT_ID, val));
    });
  };

  handleActionChange = action => {
    if (
      action === TEARDOWN &&
      ![USA, IRE, SGP].includes(this.props.getFieldValue(RETURN_TO_TYPE))
    ) {
      this.props.setFieldValue(RETURN_TO_TYPE, null);
    }
  };

  renderLineItems = ({ fields, lineItems }) => {
    const filteredActions = removeKey(ACTIONS_REQUESTED, EXPECTED_COMPONENT);

    return (
      <tbody>
        {fields.map((item, idx) => {
          const lineItem = lineItems[idx];
          const lineItemLength = lineItems.length;
          const repairStation =
            _.get(
              lineItem,
              "purchase_order_line_item.preferred_repair_station"
            ) || "None";
          const cap =
            _.get(lineItem, CAP_TYPE) &&
            `| ${CAP_TYPES[_.get(lineItem, CAP_TYPE)].label} Cap: ${dollars(
              _.get(lineItem, "cap_amount")
            )}`;

          return (
            <tr key={`item-${lineItem.id}`}>
              <td>{idx + 1}</td>
              <td>
                <table className="table">
                  <tbody>
                    <td>
                      {lineItem.part.part_number} | in&nbsp;
                      {INVENTORIES.condition[lineItem.condition].label}{" "}
                      Condition |{lineItem.quantity} QTY @{" "}
                      {dollars(lineItem.cost_per_unit)} / each {cap}
                      {lineItemLength > 1 && (
                        <React.Fragment>
                          <br />
                          Suggested Repair Station: {repairStation}
                          <p />
                        </React.Fragment>
                      )}
                    </td>
                    <td className="text-right">{dollars(lineItem.subtotal)}</td>
                    <tr>
                      <td>
                        <FormRow>
                          <Col md={12}>
                            <ActionRequestedSection
                              onActionChange={(ev, action) =>
                                this.handleActionChange(action)}
                              formName={FORM_NAME}
                              index={idx}
                              setField={this.props.setFieldValue}
                              getField={this.props.getFieldValue}
                              nested
                              actions={filteredActions}
                              showEvaluationFields={false}
                            />
                          </Col>
                        </FormRow>
                        <FormRow>
                          <Col md={12}>
                            <FormSection name={`line_items[${idx}]`}>
                              <Fields
                                names={[
                                  FAA,
                                  EASA,
                                  UK_CAA,
                                  CAAC,
                                  TCCA,
                                  OTHER,
                                  OTHER_TAG_TYPE
                                ]}
                                component={TagTypes}
                                formName={FORM_NAME}
                                required
                              />
                            </FormSection>
                          </Col>
                        </FormRow>
                        <FormRow>
                          <Col md={6}>
                            <Field
                              name={`line_items[${idx}].internal_note`}
                              rows={5}
                              cols={50}
                              component={TextArea}
                              label={properCase(INTERNAL_NOTE)}
                            />
                          </Col>
                          <Col md={6}>
                            <Field
                              name={`line_items[${idx}].external_note`}
                              rows={5}
                              cols={50}
                              component={TextArea}
                              label={properCase(EXTERNAL_NOTE)}
                            />
                          </Col>
                        </FormRow>
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          );
        })}
      </tbody>
    );
  };

  render() {
    const {
      formName,
      lineItems,
      getFieldValue,
      setFieldValue,
      returnToCompany,
      selectedCompany
    } = this.props;

    const { invalidCompany, addresses } = this.state;
    const containsTeardown = lineItems.some(
      (i, idx) =>
        getFieldValue(`line_items[${idx}].action_requested`) === TEARDOWN
    );
    let returnToTypes = [COMPANY, DROPSHIP, DO_NOT_SHIP];
    let hasReturnToLocations = true;

    if (
      !isEmpty(getCreditRequestedWarrantyRmas(lineItems, "customer_rma")) ||
      containsTeardown
    ) {
      returnToTypes = [];
    }

    if (!isEmpty(getWarrantyAndNotCreditRmas(lineItems, "customer_rma"))) {
      returnToTypes = [COMPANY, DROPSHIP];
      hasReturnToLocations = false;
    }

    const repairStatus =
      lineItems &&
      lineItems[0] &&
      [REPAIR_EVALUATION_APPROVED, REPAIR_EVALUATION_REJECTED].includes(
        lineItems[0].repair_status
      );

    const firstLineItem = lineItems.length ? lineItems[0] : {};
    const lineItemsLength = lineItems.length;
    const repairStation =
      _.get(
        firstLineItem,
        "purchase_order_line_item.preferred_repair_station"
      ) || "None";

    return (
      <AppForm {...this.props} disabled={invalidCompany}>
        <AppForm.Body>
          <DoNotDoBusinessWarning company={selectedCompany} />
          {lineItemsLength === 1 && (
            <FormRow>
              <Col md={6}>
                <Warning>Suggested Repair Station: {repairStation}</Warning>
              </Col>
            </FormRow>
          )}
          <MultiLocationFeature>
            <LocationWarning lineItems={lineItems} />
          </MultiLocationFeature>
          <SearchCompany
            companySearch={searchRepairStationCompanies}
            contacts={this.state.contacts}
            formName={formName}
            fetchContacts={this.fetchContacts}
            selectedCompany={selectedCompany}
            onCompanySelected={this.handleCompanySelected}
            onCompanyToggleTypeChange={this.handleToggleChange}
            type="repairOrder"
            required
          />
          <FormRow>
            <Col md={6}>
              <PaymentTerms />
            </Col>
          </FormRow>
          <DashedSeparator />
          <h4>Ship To</h4>
          <FormRow>
            <Col md={6}>
              <div className="form-group">
                <Field
                  name={SHIP_TO_FOB}
                  component={FormInput}
                  label="FOB"
                  required
                />
              </div>
            </Col>
          </FormRow>
          <ShippingFields
            formName={formName}
            shippingFields={[
              SHIP_TO_TYPE,
              SHIP_TO_COMPANY_ID,
              SHIP_TO_COMPANY_ADDRESS_ID
            ]}
            setFOB={val => setFieldValue(RETURN_TO_FOB, val)}
            setTenantAccount
            repairStationOptions={addresses}
            shippingTypes={[REPAIR_STATION, COMPANY, DROPSHIP]}
            shippingMethod={SHIP_TO_SHIPPING_METHOD}
            shippingAddressField={SHIP_TO_SHIPPING_ADDRESS}
            hasLocations={false}
          />
          <h4>Return To</h4>
          <ShippingFields
            formName={formName}
            path={RETURN_TO}
            shippingFields={[
              RETURN_TO_TYPE,
              RETURN_TO_COMPANY_ID,
              RETURN_TO_COMPANY_ADDRESS_ID
            ]}
            shipToCompany={returnToCompany}
            shippingTypes={returnToTypes}
            shippingMethod={RETURN_TO_SHIPPING_METHOD}
            shippingAddressField={RETURN_TO_SHIPPING_ADDRESS}
            required={false}
            ship_to_fields_required={repairStatus}
            hasLocations={hasReturnToLocations}
            locationFieldName={RETURN_TO_LOCATION_ID}
          />
          <FormRow>
            <Col md={6}>
              <div className="form-group">
                <Field
                  name={RETURN_TO_FOB}
                  component={FormInput}
                  label="Return To FOB"
                  required
                />
              </div>
            </Col>
          </FormRow>
          <div>
            <div
              className="row row-col-gutters-half"
              style={{ marginTop: "26px" }}
            >
              <Col md={12}>
                <table className="table table-striped table-numbered margin-bottom-none">
                  <FieldArray
                    name={LINE_ITEMS}
                    component={this.renderLineItems}
                    lineItems={lineItems}
                  />
                </table>
              </Col>
            </div>
          </div>
        </AppForm.Body>
      </AppForm>
    );
  }
}

Form.propTypes = {
  change: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
  getFieldValue: PropTypes.func.isRequired,
  lineItems: PropTypes.arrayOf(PropTypes.shape({})),
  setFieldValue: PropTypes.func.isRequired,
  selectedCompany: PropTypes.instanceOf(Object).isRequired,
  setSelectedCompany: PropTypes.func.isRequired
};

export default connect(
  state => ({
    getFieldValue: field => formValueSelector(FORM_NAME)(state, field)
  }),
  dispatch => ({
    onSubmit: data => dispatch(convertRo(data)),
    queryParts: filter => dispatch(searchPartsByStatus(filter)),
    searchInventories: (partId, filters) =>
      dispatch(queryInventories(partId, filters)),
    setFieldValue: (field, val) => dispatch(change(FORM_NAME, field, val))
  })
)(
  form({
    form: FORM_NAME,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/RepairOrders/components/ConvertToRo/Form.jsx